import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Typography,
  FormGroup,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useMapState } from "contexts/MapStateContext";
import {
  addRiskMapSource,
  addRiskMapLayers,
  updateLayerVisibility,
  updateScenarioFilters,
} from "utils/riskMap";
import { TILE_VERSIONS } from "constants/tileVersions";

/**
 * StatewideControl component provides a complete control widget for Scenario Display and Statewide Layers.
 * This component is shared across Vegetation, Urban, and Risk maps.
 *
 * @param {string} tileVersion - The tile version to use (e.g., '2025v2', '2025v1r1')
 * @param {boolean} compareMode - Whether compare mode is active (RiskMap only)
 * @param {string} scenario - Controlled scenario value (optional)
 * @param {function} onScenarioChange - Callback for scenario changes (optional)
 * @param {array} layers - Controlled layers value (optional)
 * @param {function} onLayersChange - Callback for layers changes (optional)
 * @param {object} mapInstance - Optional map instance to use instead of context (for compare mode)
 * @param {boolean} skipMapUpdates - If true, skip automatic map updates (for compare mode)
 * @param {object} sx - Additional MUI sx props for positioning the ControlWidget
 */
const StatewideControl = ({
  tileVersion = TILE_VERSIONS[0].value,
  compareMode = false,
  scenario: controlledScenario,
  onScenarioChange,
  layers: controlledLayers,
  onLayersChange,
  mapInstance,
  skipMapUpdates = false,
}) => {
  const { map: contextMap } = useMapState();
  const map = mapInstance || contextMap;

  const [isMapReady, setIsMapReady] = useState(false);

  // Internal state for uncontrolled mode
  const [internalScenario, setInternalScenario] = useState("combined");
  const [internalLayers, setInternalLayers] = useState([]);

  // Use controlled values if provided, otherwise use internal state
  const tilesScenario =
    controlledScenario !== undefined ? controlledScenario : internalScenario;
  const tilesFilterLayers =
    controlledLayers !== undefined ? controlledLayers : internalLayers;

  const setTilesScenario = (value) => {
    if (onScenarioChange) {
      onScenarioChange(value);
    } else {
      setInternalScenario(value);
    }
  };

  const setTilesFilterLayers = (value) => {
    if (onLayersChange) {
      onLayersChange(value);
    } else {
      setInternalLayers(value);
    }
  };

  // Track if map is ready
  useEffect(() => {
    if (!map) return;

    const checkAndSetReady = () => {
      if (map.isStyleLoaded()) {
        setIsMapReady(true);
        setTimeout(() => map.resize(), 0);
      }
    };

    // Check immediately
    checkAndSetReady();

    // Also listen for the style to load if not already loaded
    if (!map.isStyleLoaded()) {
      map.on("load", checkAndSetReady);
      map.on("styledata", checkAndSetReady);
    }

    return () => {
      if (map) {
        map.off("load", checkAndSetReady);
        map.off("styledata", checkAndSetReady);
      }
    };
  }, [map]);

  // Add risk map source and layers when map is ready
  useEffect(() => {
    if (!map || !isMapReady || compareMode || skipMapUpdates) return;

    try {
      const sourceChanged = addRiskMapSource(map, tileVersion);
      if (sourceChanged || !map.getLayer("buildings")) {
        addRiskMapLayers(map, tilesScenario, tilesFilterLayers);
      }
    } catch (error) {
      console.error("Error adding risk map layers:", error);
    }
  }, [
    map,
    isMapReady,
    tilesFilterLayers,
    tilesScenario,
    tileVersion,
    compareMode,
    skipMapUpdates,
  ]);

  // Update layer visibility when filters change
  useEffect(() => {
    if (!map || !isMapReady || compareMode || skipMapUpdates) return;
    updateLayerVisibility(map, tilesFilterLayers);
  }, [map, isMapReady, tilesFilterLayers, compareMode, skipMapUpdates]);

  // Update scenario filters when scenario changes
  useEffect(() => {
    if (!map || !isMapReady || compareMode || skipMapUpdates) return;
    updateScenarioFilters(map, tilesScenario);
  }, [map, isMapReady, tilesScenario, compareMode, skipMapUpdates]);

  return (
    <>
      <Typography variant="h6">
        Tile Version:{" "}
        {TILE_VERSIONS?.find((v) => v.value === tileVersion)?.label ||
          tileVersion}
      </Typography>
      <Typography variant="h6">Scenario Display</Typography>
      <RadioGroup
        value={tilesScenario}
        onChange={(e) => setTilesScenario(e.target.value)}
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          value="combined"
          control={<Radio />}
          label="Combined Scenarios"
          labelPlacement="start"
          sx={{
            margin: 0,
            width: "100%",
            justifyContent: "space-between",
          }}
        />
        <FormControlLabel
          value="north-east"
          control={<Radio />}
          label="North/East Scenario"
          labelPlacement="start"
          sx={{
            margin: 0,
            width: "100%",
            justifyContent: "space-between",
          }}
        />
        <FormControlLabel
          value="south-west"
          control={<Radio />}
          label="South/West Scenario"
          labelPlacement="start"
          sx={{
            margin: 0,
            width: "100%",
            justifyContent: "space-between",
          }}
        />
      </RadioGroup>

      <Typography variant="h6">Statewide Layers</Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={tilesFilterLayers.includes("risk-rating")}
              onChange={(e) => {
                const layers = e.target.checked
                  ? [...tilesFilterLayers, "risk-rating"]
                  : tilesFilterLayers.filter((l) => l !== "risk-rating");
                setTilesFilterLayers(layers);
              }}
              color="primary"
            />
          }
          label="Risk Rating"
          labelPlacement="start"
          sx={{
            margin: 0,
            width: "100%",
            justifyContent: "space-between",
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={tilesFilterLayers.includes("fire-pathways")}
              onChange={(e) => {
                const layers = e.target.checked
                  ? [...tilesFilterLayers, "fire-pathways"]
                  : tilesFilterLayers.filter((l) => l !== "fire-pathways");
                setTilesFilterLayers(layers);
              }}
              color="primary"
            />
          }
          label="Fire Pathways"
          labelPlacement="start"
          sx={{
            margin: 0,
            width: "100%",
            justifyContent: "space-between",
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={tilesFilterLayers.includes("conflagration-blocks")}
              onChange={(e) => {
                const layers = e.target.checked
                  ? [...tilesFilterLayers, "conflagration-blocks"]
                  : tilesFilterLayers.filter(
                      (l) => l !== "conflagration-blocks"
                    );
                setTilesFilterLayers(layers);
              }}
              color="primary"
            />
          }
          label="Conflagration Blocks"
          labelPlacement="start"
          sx={{
            margin: 0,
            width: "100%",
            justifyContent: "space-between",
          }}
        />
      </FormGroup>
    </>
  );
};

export default StatewideControl;
